import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AssignedOpening } from '../components/quality/AssignedOpening';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useContentProtection } from '../hooks/useContentProtection';
import {
    Search,
    Users,
    Target,
    Trophy,
    Briefcase,
    Building2,
    TrendingUp,
    Clock,
    Play,
    ChevronRight,
    Award,
    Loader2,
    Filter,
    BookOpen,
    Brain,
    Sparkles,
    CheckCircle,
    Flame,
    Crown,
    Medal,
    Star,
    Zap,
    Calendar,
    Shield,
    Gem,
    Globe,
    Lock,
    Volume2
} from 'lucide-react';
import MiniCommunicationScoreCard from '../components/communication/MiniCommunicationScoreCard';

// Badge icons mapping
const BADGE_ICONS = {
    'trophy': Trophy,
    'star': Star,
    'award': Award,
    'medal': Medal,
    'crown': Crown,
    'check-circle': CheckCircle,
    'zap': Zap,
    'flame': Flame,
    'sparkles': Sparkles,
    'target': Target,
    'trending-up': TrendingUp,
    'calendar': Calendar,
    'shield': Shield,
    'gem': Gem,
    'globe': Globe,
    'search': Search,
    'users': Users
};

// Badge Icon Component
const BadgeIcon = ({ icon, className }) => {
    const IconComponent = BADGE_ICONS[icon] || Award;
    return <IconComponent className={className} />;
};

const SourcingLabs = () => {
    const { api, user: currentUser } = useAuth();
    const navigate = useNavigate();
    
    // Enable content protection (disable right-click, copy, screenshots)
    useContentProtection(true);
    
    const [loading, setLoading] = useState(true);
    const [labs, setLabs] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeSession, setActiveSession] = useState(null);
    const [selectedIndustry, setSelectedIndustry] = useState('all');
    const [startingLab, setStartingLab] = useState(null);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    
    // Gamification state
    const [gamificationProgress, setGamificationProgress] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [badges, setBadges] = useState(null);
    const [leaderboardPeriod, setLeaderboardPeriod] = useState('all');
    const [activeTab, setActiveTab] = useState('labs');

    // Memoized fetch functions
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = selectedIndustry !== 'all' ? `?industry=${selectedIndustry}` : '';
            const [labsRes, statsRes, activeRes, progressRes] = await Promise.all([
                api.get(`/sourcing-labs/${params}`),
                api.get('/sourcing-labs/stats/me'),
                api.get('/sourcing-labs/sessions/active').catch(err => {
                    console.warn('Failed to fetch active session:', err);
                    return { data: null };
                }),
                api.get('/sourcing-labs/gamification/my-progress').catch(err => {
                    console.warn('Failed to fetch progress:', err);
                    return { data: null };
                })
            ]);
            setLabs(labsRes.data);
            setStats(statsRes.data);
            setActiveSession(activeRes.data);
            setGamificationProgress(progressRes.data);
        } catch (error) {
            console.error('Failed to fetch sourcing labs data:', error);
            if (error.response?.status === 403) {
                toast.error('Sourcing Labs feature is disabled');
            } else {
                toast.error('Failed to load Sourcing Labs', {
                    description: 'Please refresh the page or try again later.',
                    duration: 5000
                });
            }
        } finally {
            setLoading(false);
        }
    }, [api, selectedIndustry]);

    const fetchLeaderboard = useCallback(async () => {
        try {
            const response = await api.get(`/sourcing-labs/gamification/leaderboard?period=${leaderboardPeriod}`);
            setLeaderboard(response.data);
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error);
        }
    }, [api, leaderboardPeriod]);

    const fetchBadges = useCallback(async () => {
        try {
            const response = await api.get('/sourcing-labs/gamification/badges');
            setBadges(response.data);
        } catch (error) {
            console.error('Failed to fetch badges:', error);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (activeTab === 'leaderboard') {
            fetchLeaderboard();
        }
    }, [activeTab, fetchLeaderboard]);

    useEffect(() => {
        if (activeTab === 'badges' && !badges) {
            fetchBadges();
        }
    }, [activeTab, badges, fetchBadges]);

    const startSession = async (labId, difficulty = null, assignedTask = null) => {
        // Prevent double-clicks
        if (startingLab || showLoadingModal) return;
        const sessionPath = (id) => `/sourcing-labs/session/${id}${assignedTask ? `?qt=${assignedTask}` : ''}`;
        
        setStartingLab(labId);
        setShowLoadingModal(true);
        setLoadingMessage('Initializing Sourcing Lab...');
        
        try {
            setLoadingMessage('Generating job brief and candidates...');
            const payload = { lab_id: labId };
            if (difficulty) {
                payload.difficulty = difficulty;
            }
            const response = await api.post('/sourcing-labs/sessions/start', payload);
            
            if (!response.data?.id) {
                throw new Error('No session ID returned');
            }
            
            setLoadingMessage('Loading session...');
            toast.success('Session started!');
            navigate(sessionPath(response.data.id));
        } catch (error) {
            console.error('Failed to start session:', error);
            const errorDetail = error.response?.data?.detail;
            let errorMsg = 'Failed to start session';
            
            // If there's an active session, offer to continue it
            if (errorDetail?.includes?.('active session') || error.response?.status === 409) {
                try {
                    setLoadingMessage('Found existing session, loading...');
                    const activeRes = await api.get('/sourcing-labs/sessions/active');
                    if (activeRes.data?.id && (!assignedTask || activeRes.data.lab_id === labId)) {
                        toast.info('Continuing your existing session');
                        navigate(sessionPath(activeRes.data.id));
                        return;
                    }
                } catch (activeErr) {
                    console.error('Failed to fetch active session:', activeErr);
                }
            }
            
            if (errorDetail) {
                errorMsg = typeof errorDetail === 'object' ? errorDetail.message || JSON.stringify(errorDetail) : errorDetail;
            }
            
            setShowLoadingModal(false);
            if (assignedTask) setAssignedFailed(true);
            toast.error(errorMsg, {
                description: 'Please try again or contact support if the issue persists.',
                duration: 5000
            });
        } finally {
            setStartingLab(null);
        }
    };

    // Quality Task deep link: /sourcing-labs?start=<lab_id>&qt=<task_id> launches only that lab
    const [searchParams, setSearchParams] = useSearchParams();
    const [assignedStart] = useState(() => searchParams.get('qt') && searchParams.get('start'));
    const [assignedFailed, setAssignedFailed] = useState(false);
    const autoStartRef = useRef(false);
    useEffect(() => {
        const labId = searchParams.get('start');
        if (!labId || loading || autoStartRef.current) return;
        autoStartRef.current = true;
        const assignedTask = searchParams.get('qt');
        setSearchParams({}, { replace: true });
        startSession(labId, null, assignedTask);
    }, [searchParams, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const continueSession = () => {
        if (activeSession) {
            navigate(`/sourcing-labs/session/${activeSession.id}`);
        }
    };

    const getIndustryIcon = (industry) => {
        const icons = {
            'information_technology': <Brain className="h-5 w-5" />,
            'human_resources': <Users className="h-5 w-5" />,
            'finance': <Building2 className="h-5 w-5" />,
            'sales': <TrendingUp className="h-5 w-5" />,
            'marketing': <Sparkles className="h-5 w-5" />,
            'manufacturing': <Briefcase className="h-5 w-5" />
        };
        return icons[industry] || <Target className="h-5 w-5" />;
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            'beginner': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
            'intermediate': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
            'advanced': 'bg-amber-500/10 text-amber-600 border-amber-500/30',
            'expert': 'bg-red-500/10 text-red-600 border-red-500/30'
        };
        return colors[difficulty] || colors['beginner'];
    };

    if (loading || assignedStart) {
        return (
            <MainLayout>
                {assignedStart ? <AssignedOpening failed={assignedFailed} label="Sourcing Lab" /> : (
                    <div className="flex items-center justify-center h-[60vh]">
                        <Loader2 className="h-12 w-12 animate-spin text-primary" />
                    </div>
                )}
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="sourcing-labs-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Search className="h-8 w-8 text-primary" />
                            Sourcing Labs
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Master the art of candidate sourcing through hands-on practice
                        </p>
                    </div>
                    
                    {/* Streak & Quick Stats */}
                    <div className="flex items-center gap-4">
                        {gamificationProgress?.streak > 0 && (
                            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                                <Flame className="h-5 w-5 text-amber-500" />
                                <div>
                                    <p className="text-sm font-bold text-amber-600">{gamificationProgress.streak} Day Streak</p>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 border">
                            <Award className="h-5 w-5 text-primary" />
                            <div>
                                <p className="text-sm font-bold">{gamificationProgress?.badges_earned || 0}/{gamificationProgress?.total_badges || 0}</p>
                                <p className="text-xs text-muted-foreground">Badges</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Active Session Banner */}
                {activeSession && (
                    <Card className="border-primary/50 bg-primary/5">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-xl bg-primary/10">
                                        <Play className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-medium">You have an active session</p>
                                        <p className="text-sm text-muted-foreground">
                                            {activeSession.job_brief?.title} - {activeSession.difficulty} difficulty
                                        </p>
                                    </div>
                                </div>
                                <Button onClick={continueSession} data-testid="continue-session-btn">
                                    Continue Session
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tabs for Labs / Leaderboard / Badges */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="grid w-full max-w-md grid-cols-3">
                        <TabsTrigger value="labs" className="flex items-center gap-2" data-testid="sourcing-tab-labs">
                            <BookOpen className="h-4 w-4" />
                            Labs
                        </TabsTrigger>
                        <TabsTrigger value="leaderboard" className="flex items-center gap-2" data-testid="sourcing-tab-leaderboard">
                            <Trophy className="h-4 w-4" />
                            Leaderboard
                        </TabsTrigger>
                        <TabsTrigger value="badges" className="flex items-center gap-2" data-testid="sourcing-tab-badges">
                            <Award className="h-4 w-4" />
                            Badges
                        </TabsTrigger>
                    </TabsList>

                    {/* Labs Tab */}
                    <TabsContent value="labs" className="space-y-6">
                        {/* Communication Score Card */}
                        <MiniCommunicationScoreCard moduleName="sourcing_labs" />

                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Sessions Completed</p>
                                            <p className="text-2xl font-bold">{stats?.total_sessions || 0}</p>
                                        </div>
                                        <Trophy className="h-8 w-8 text-amber-500/30" />
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Average Score</p>
                                            <p className="text-2xl font-bold text-primary">{stats?.average_score || 0}%</p>
                                        </div>
                                        <Target className="h-8 w-8 text-primary/30" />
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Highest Score</p>
                                            <p className="text-2xl font-bold text-emerald-500">{stats?.highest_score || 0}%</p>
                                        </div>
                                        <Award className="h-8 w-8 text-emerald-500/30" />
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Difficulty Level</p>
                                            <p className="text-2xl font-bold capitalize">{stats?.highest_difficulty_reached || 'Beginner'}</p>
                                        </div>
                                        <TrendingUp className="h-8 w-8 text-blue-500/30" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Next Badges Progress */}
                        {gamificationProgress?.next_badges?.length > 0 && (
                            <Card className="border-dashed">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <Target className="h-4 w-4 text-primary" />
                                        Next Achievements
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {gamificationProgress.next_badges.map((badge, idx) => (
                                            <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <BadgeIcon icon={badge.icon} className="h-5 w-5 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate">{badge.name}</p>
                                                    <Progress value={badge.percent} className="h-1.5 mt-1" />
                                                    <p className="text-xs text-muted-foreground mt-0.5">
                                                        {badge.progress}/{badge.required}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Industry Filter */}
                        <div className="flex justify-end">
                            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                                <SelectTrigger className="w-[200px]">
                                    <Filter className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder="Filter by industry" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Industries</SelectItem>
                                    <SelectItem value="information_technology">Information Technology</SelectItem>
                                    <SelectItem value="human_resources">Human Resources</SelectItem>
                                    <SelectItem value="finance">Finance</SelectItem>
                                    <SelectItem value="sales">Sales</SelectItem>
                                    <SelectItem value="marketing">Marketing</SelectItem>
                                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Labs Grid */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {labs.map((lab) => (
                                <Card 
                                    key={lab.id} 
                                    className="hover:shadow-lg transition-all hover:border-primary/50"
                                    data-testid={`lab-card-${lab.id}`}
                                >
                                    <CardHeader className="pb-3">
                                        <div className="flex items-start justify-between">
                                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                                {getIndustryIcon(lab.industry)}
                                            </div>
                                            {lab.user_completed_count > 0 && (
                                                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                    {lab.user_completed_count} completed
                                                </Badge>
                                            )}
                                        </div>
                                        <CardTitle className="text-lg mt-3">{lab.name}</CardTitle>
                                        <CardDescription className="line-clamp-2">
                                            {lab.description}
                                        </CardDescription>
                                    </CardHeader>
                                    
                                    <CardContent className="space-y-4">
                                        {/* Lab Info */}
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-4 w-4" />
                                                {lab.estimated_duration_minutes} min
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Users className="h-4 w-4" />
                                                {lab.times_completed} attempts
                                            </span>
                                        </div>

                                        {/* Difficulty Levels */}
                                        <div className="flex flex-wrap gap-1">
                                            {lab.difficulty_levels_available?.map((diff) => (
                                                <Badge 
                                                    key={diff} 
                                                    variant="outline" 
                                                    className={`text-xs capitalize ${getDifficultyColor(diff)}`}
                                                >
                                                    {diff}
                                                </Badge>
                                            ))}
                                        </div>

                                        {/* Skills */}
                                        <div>
                                            <p className="text-xs text-muted-foreground mb-2">Skills Practiced</p>
                                            <div className="flex flex-wrap gap-1">
                                                {lab.skills_practiced?.slice(0, 3).map((skill, idx) => (
                                                    <Badge key={idx} variant="secondary" className="text-xs">
                                                        {skill}
                                                    </Badge>
                                                ))}
                                                {lab.skills_practiced?.length > 3 && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        +{lab.skills_practiced.length - 3}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>

                                        {/* Start Button */}
                                        <Button 
                                            className="w-full" 
                                            onClick={() => startSession(lab.id)}
                                            disabled={startingLab === lab.id}
                                            data-testid={`start-lab-${lab.id}`}
                                        >
                                            {startingLab === lab.id ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Starting...
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="h-4 w-4 mr-2" />
                                                    Start Lab
                                                </>
                                            )}
                                        </Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* Leaderboard Tab */}
                    <TabsContent value="leaderboard" className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <Trophy className="h-6 w-6 text-amber-500" />
                                Top Sourcers
                            </h2>
                            <Select value={leaderboardPeriod} onValueChange={setLeaderboardPeriod}>
                                <SelectTrigger className="w-[150px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Time</SelectItem>
                                    <SelectItem value="month">This Month</SelectItem>
                                    <SelectItem value="week">This Week</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {leaderboard ? (
                            <div className="space-y-4">
                                {/* Empty State */}
                                {leaderboard.leaderboard?.length === 0 && (
                                    <Card className="border-dashed">
                                        <CardContent className="flex flex-col items-center justify-center py-16">
                                            <div className="w-20 h-20 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                                                <Trophy className="h-10 w-10 text-amber-500/50" />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No Top Sourcers Yet</h3>
                                            <p className="text-muted-foreground text-center max-w-sm">
                                                Be the first to complete a sourcing lab and claim the #1 spot on the leaderboard!
                                            </p>
                                            <Button 
                                                className="mt-6" 
                                                onClick={() => setActiveTab('labs')}
                                            >
                                                <Play className="h-4 w-4 mr-2" />
                                                Start a Lab Now
                                            </Button>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Top 3 Podium */}
                                {leaderboard.leaderboard?.length >= 3 && (
                                    <div className="grid grid-cols-3 gap-4 mb-8">
                                        {/* 2nd Place */}
                                        <div className="flex flex-col items-center pt-8">
                                            <div className="relative">
                                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white font-bold text-xl">
                                                    {leaderboard.leaderboard[1]?.user_name?.charAt(0) || '?'}
                                                </div>
                                                <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-slate-400 flex items-center justify-center text-white font-bold text-sm border-2 border-background">
                                                    2
                                                </div>
                                            </div>
                                            <p className="font-medium mt-3 text-sm truncate max-w-full">{leaderboard.leaderboard[1]?.user_name}</p>
                                            <p className="text-primary font-bold">{Math.round(leaderboard.leaderboard[1]?.total_score)}</p>
                                            <p className="text-xs text-muted-foreground">{leaderboard.leaderboard[1]?.sessions_completed} sessions</p>
                                        </div>
                                        
                                        {/* 1st Place */}
                                        <div className="flex flex-col items-center">
                                            <Crown className="h-8 w-8 text-amber-500 mb-2" />
                                            <div className="relative">
                                                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-2xl ring-4 ring-amber-500/30">
                                                    {leaderboard.leaderboard[0]?.user_name?.charAt(0) || '?'}
                                                </div>
                                                <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm border-2 border-background">
                                                    1
                                                </div>
                                            </div>
                                            <p className="font-semibold mt-3 truncate max-w-full">{leaderboard.leaderboard[0]?.user_name}</p>
                                            <p className="text-primary font-bold text-lg">{Math.round(leaderboard.leaderboard[0]?.total_score)}</p>
                                            <p className="text-xs text-muted-foreground">{leaderboard.leaderboard[0]?.sessions_completed} sessions</p>
                                        </div>
                                        
                                        {/* 3rd Place */}
                                        <div className="flex flex-col items-center pt-12">
                                            <div className="relative">
                                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-white font-bold text-lg">
                                                    {leaderboard.leaderboard[2]?.user_name?.charAt(0) || '?'}
                                                </div>
                                                <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-amber-700 flex items-center justify-center text-white font-bold text-xs border-2 border-background">
                                                    3
                                                </div>
                                            </div>
                                            <p className="font-medium mt-3 text-sm truncate max-w-full">{leaderboard.leaderboard[2]?.user_name}</p>
                                            <p className="text-primary font-bold">{Math.round(leaderboard.leaderboard[2]?.total_score)}</p>
                                            <p className="text-xs text-muted-foreground">{leaderboard.leaderboard[2]?.sessions_completed} sessions</p>
                                        </div>
                                    </div>
                                )}

                                {/* Leaderboard List */}
                                <Card>
                                    <CardContent className="p-0">
                                        <ScrollArea className="h-[400px]">
                                            <div className="divide-y">
                                                {leaderboard.leaderboard?.map((entry, idx) => (
                                                    <div 
                                                        key={entry.user_id}
                                                        className={`flex items-center gap-4 p-4 ${entry.is_current_user ? 'bg-primary/5' : ''}`}
                                                    >
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                                            idx === 0 ? 'bg-amber-500 text-white' :
                                                            idx === 1 ? 'bg-slate-400 text-white' :
                                                            idx === 2 ? 'bg-amber-700 text-white' :
                                                            'bg-muted text-muted-foreground'
                                                        }`}>
                                                            {entry.rank}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-medium flex items-center gap-2">
                                                                {entry.user_name}
                                                                {entry.is_current_user && (
                                                                    <Badge variant="outline" className="text-xs">You</Badge>
                                                                )}
                                                                {entry.streak > 0 && (
                                                                    <span className="flex items-center gap-1 text-amber-500 text-xs">
                                                                        <Flame className="h-3 w-3" />
                                                                        {entry.streak}
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {entry.sessions_completed} sessions - Avg: {Math.round(entry.avg_score)}%
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-bold text-primary">{Math.round(entry.total_score)}</p>
                                                            <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                                                <Award className="h-3 w-3" />
                                                                {entry.badge_count}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </ScrollArea>
                                    </CardContent>
                                </Card>

                                {/* Current User Rank */}
                                {leaderboard.current_user_rank && (
                                    <Card className="border-primary/50">
                                        <CardContent className="p-4 flex items-center justify-between">
                                            <p className="text-sm">Your current rank</p>
                                            <p className="text-2xl font-bold text-primary">#{leaderboard.current_user_rank}</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        )}
                    </TabsContent>

                    {/* Badges Tab */}
                    <TabsContent value="badges" className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <Award className="h-6 w-6 text-primary" />
                                Achievement Badges
                            </h2>
                            <Badge variant="outline" className="text-lg px-4 py-1">
                                {badges?.earned_count || 0} / {badges?.total_badges || 0}
                            </Badge>
                        </div>

                        {badges ? (
                            <div className="space-y-8">
                                {Object.entries(badges.badges_by_category || {}).map(([category, categoryBadges]) => (
                                    <div key={category}>
                                        <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-4 capitalize">
                                            {category.replace('_', ' ')} Badges
                                        </h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                            {categoryBadges.map((badge) => (
                                                <Card 
                                                    key={badge.id}
                                                    className={`text-center transition-all ${
                                                        badge.earned 
                                                            ? 'border-primary/50 bg-primary/5' 
                                                            : 'opacity-50 grayscale'
                                                    }`}
                                                >
                                                    <CardContent className="p-4">
                                                        <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                                                            badge.earned 
                                                                ? 'bg-gradient-to-br from-primary/20 to-primary/10' 
                                                                : 'bg-muted'
                                                        }`}>
                                                            {badge.earned ? (
                                                                <BadgeIcon icon={badge.icon} className="h-6 w-6 text-primary" />
                                                            ) : (
                                                                <Lock className="h-5 w-5 text-muted-foreground" />
                                                            )}
                                                        </div>
                                                        <p className="font-medium text-sm">{badge.name}</p>
                                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                            {badge.description}
                                                        </p>
                                                        {badge.earned && (
                                                            <Badge variant="outline" className="mt-2 text-xs bg-emerald-500/10 text-emerald-600">
                                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                                Earned
                                                            </Badge>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
            
            {/* Loading Modal - Prevents double-clicks */}
            <Dialog open={showLoadingModal} onOpenChange={() => {}}>
                <DialogContent className="sm:max-w-md" hideClose>
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                        <div className="relative">
                            <Loader2 className="h-12 w-12 animate-spin text-primary" />
                        </div>
                        <div className="text-center space-y-2">
                            <h3 className="font-semibold text-lg">Loading Sourcing Lab</h3>
                            <p className="text-sm text-muted-foreground">{loadingMessage}</p>
                            <p className="text-xs text-muted-foreground">This may take 5-10 seconds...</p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </MainLayout>
    );
};

export default SourcingLabs;
